/**
 * Copyright © 2026 Delicious AI, LLC
 */

import {
  AddCircleOutline,
  CheckCircleOutline,
  EditOutlined,
  FlagOutlined,
  HelpOutline,
  RestartAlt,
  Search,
  Close,
} from '@mui/icons-material'
import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import {
  FLAG_CLASSIFICATION_CROP,
  SABRE_GET_PRODUCT,
  SABRE_GET_PRODUCTS_SEARCH,
  SabreGetProductsSearchQuery,
  SabreGetProductsSearchQueryVariables,
  UltraFlagClassificationCropMutation,
  UltraFlagClassificationCropMutationVariables,
  UltraFlaggedTypeChoices,
} from '@dai/graphql'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import useT from '@/i18n/useT'
import { usePageHeaderActions } from '@/layouts/PageHeaderActionsContext'

import type {
  ClassificationCorrection,
  CorrectionCandidate,
} from './correctionTypes'
import CopyableIdField from './CopyableIdField'
import FlagItemModal from './FlagItemModal'
import FullImageDialog from './FullImageDialog'
import ProductDialog from './ProductDialog'
import {
  getProductDisplayName,
  getSearchProductDisplayName,
  ProductByUpcData,
  ProductByUpcVariables,
  SearchProduct,
} from './labelingHelpers'
import ProductImageBox from './ProductImageBox'
import type { ReviewMetadata } from '@/graphql'
import type { ReviewCorrectionLocationState } from './review/reviewNavigation'
import ReviewFeedbackNotice from './review/ReviewFeedbackNotice'
import ReviewVerificationChips from './review/ReviewVerificationChips'
import { useCorrectionWorkflow } from './useCorrectionWorkflow'

const getCorrectionCandidateDisplayName = (
  candidate: CorrectionCandidate | null,
) => {
  if (!candidate) return null

  return [candidate.name, candidate.packaging, candidate.brandName]
    .filter(Boolean)
    .join(' · ')
}

type CorrectionProps = {
  initialCorrection?: ClassificationCorrection | null
  reviewMetadata?: ReviewMetadata | null
  readOnly?: boolean
  onSubmitSuccess?: () => Promise<unknown> | unknown
}

function Correction({
  initialCorrection: initialCorrectionProp = null,
  reviewMetadata: reviewMetadataProp = null,
  readOnly = false,
  onSubmitSuccess,
}: CorrectionProps) {
  const { t } = useT()
  const theme = useTheme()
  const isInlineReplacePanel = useMediaQuery(theme.breakpoints.up('md'))
  const location = useLocation()
  const reviewLocationState =
    (location.state as ReviewCorrectionLocationState | null) ?? null
  const initialCorrection =
    initialCorrectionProp ?? reviewLocationState?.reviewEditCorrection ?? null
  const reviewMetadata: ReviewMetadata | null =
    reviewMetadataProp ?? reviewLocationState?.reviewMetadata ?? null
  const {
    correction,
    crop,
    error,
    handleCropFlagged,
    handleRetry,
    handleSubmitCorrection,
    leaseLock,
    loading,
    noItems,
    selectedPreviewBox,
    setSelectedPreviewBoxId,
    submitting,
  } = useCorrectionWorkflow(initialCorrection, {
    manageLeases: !readOnly,
    manageInitialCorrectionLease: !reviewLocationState?.reviewEditCorrection,
    onSubmitSuccess,
  })

  const [replaceDrawerOpen, setReplaceDrawerOpen] = React.useState(false)
  const [productSearchInput, setProductSearchInput] = React.useState('')
  const [selectedUpc, setSelectedUpc] = React.useState<string | null>(null)
  const [selectedReplacementProduct, setSelectedReplacementProduct] =
    React.useState<SearchProduct | null>(null)
  const [productSearchOptions, setProductSearchOptions] = React.useState<
    SearchProduct[]
  >([])
  const [flagCrop, setFlagCrop] = React.useState<
    CorrectionCandidate['id'] | null
  >(null)
  const [productDialogMode, setProductDialogMode] = React.useState<
    'create' | 'edit' | null
  >(null)
  const [helpOpen, setHelpOpen] = React.useState(false)
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const latestProductSearchRequestRef = React.useRef(0)

  const [searchProducts, { loading: searchingProducts }] = useLazyQuery<
    SabreGetProductsSearchQuery,
    SabreGetProductsSearchQueryVariables
  >(SABRE_GET_PRODUCTS_SEARCH, {
    context: { endPoint: 'sabre' },
    fetchPolicy: 'network-only',
  })
  const [flagClassificationCrop] = useMutation<
    UltraFlagClassificationCropMutation,
    UltraFlagClassificationCropMutationVariables
  >(FLAG_CLASSIFICATION_CROP, {
    context: { endPoint: 'mlUltra' },
  })

  React.useEffect(() => {
    const defaultUpc =
      correction?.crop.verifiedUpc ??
      correction?.candidates[0]?.upcA ??
      correction?.crop.currentUpc ??
      null
    setSelectedUpc(defaultUpc)
    setSelectedReplacementProduct(null)
    setProductSearchInput('')
    setProductSearchOptions([])
    setSubmitError(null)
  }, [correction])

  React.useEffect(() => {
    if (!readOnly && correction) {
      setReplaceDrawerOpen(true)
    }
  }, [correction, readOnly])

  const selectedCandidate = React.useMemo(
    () =>
      correction?.candidates.find(
        candidate => candidate.upcA === selectedUpc,
      ) ?? null,
    [correction?.candidates, selectedUpc],
  )
  const visibleCandidates = React.useMemo(() => {
    const excludedUpcs = new Set(
      [
        selectedUpc,
        correction?.crop.verifiedUpc,
        correction?.crop.currentUpc,
      ].filter((value): value is string => Boolean(value)),
    )

    return (correction?.candidates ?? []).filter(
      candidate => !excludedUpcs.has(candidate.upcA),
    )
  }, [
    correction?.candidates,
    correction?.crop.currentUpc,
    correction?.crop.verifiedUpc,
    selectedUpc,
  ])

  const {
    data: productData,
    loading: productLoading,
    refetch: refetchProductByUpc,
  } = useQuery<ProductByUpcData, ProductByUpcVariables>(SABRE_GET_PRODUCT, {
    context: { endPoint: 'sabre' },
    variables: { upc: selectedUpc || '' },
    skip: !selectedUpc,
  })

  const loadProductSearchOptions = React.useCallback(
    async (query: string, requestId: number) => {
      const response = await searchProducts({
        variables: {
          input: { searchTerm: query, limit: 20 },
        },
      })

      if (latestProductSearchRequestRef.current !== requestId) {
        return
      }

      setProductSearchOptions(response.data?.products.results ?? [])
    },
    [searchProducts],
  )

  const anchorImageUrl =
    selectedReplacementProduct?.marketingImageUrl ||
    selectedCandidate?.marketingImageUrl ||
    productData?.product?.marketingImageUrl ||
    null
  const activeProductLabel = getProductDisplayName(productData?.product || null)
  const replacementProductLabel = getSearchProductDisplayName(
    selectedReplacementProduct,
  )
  const selectedCandidateLabel =
    getCorrectionCandidateDisplayName(selectedCandidate)
  const displayedProductLabel =
    replacementProductLabel ??
    activeProductLabel ??
    selectedCandidateLabel ??
    selectedUpc
  const editableProduct = productData?.product ?? null

  React.useEffect(() => {
    if (!replaceDrawerOpen) {
      setProductSearchOptions([])
      return
    }

    const query = productSearchInput.trim()
    if (!query) {
      setProductSearchOptions([])
      return
    }

    const requestId = latestProductSearchRequestRef.current + 1
    latestProductSearchRequestRef.current = requestId

    const timeoutId = window.setTimeout(() => {
      void (async () => {
        try {
          await loadProductSearchOptions(query, requestId)
        } catch (searchError) {
          if (latestProductSearchRequestRef.current !== requestId) {
            return
          }

          console.error('Failed to search correction products', searchError)
          setProductSearchOptions([])
        }
      })()
    }, 250)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [loadProductSearchOptions, productSearchInput, replaceDrawerOpen])

  const handleResetSelection = React.useCallback(() => {
    const defaultUpc =
      correction?.crop.verifiedUpc ??
      correction?.candidates[0]?.upcA ??
      correction?.crop.currentUpc ??
      null
    setSelectedUpc(defaultUpc)
    setSelectedReplacementProduct(null)
    setProductSearchInput('')
  }, [correction])

  const replaceDrawerContent = (
    <Stack
      spacing={2.5}
      sx={{ width: '100%', p: 2.5, height: '100%', minHeight: 0 }}
    >
      <Typography variant="h6">
        {t('correction.replaceDrawer.title', 'Choose corrected product')}
      </Typography>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.25}>
          <Typography variant="subtitle2">
            {t(
              'correction.replaceDrawer.currentSelection',
              'Current selection',
            )}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {displayedProductLabel ??
              t('correction.replaceDrawer.noSelection', 'No product selected')}
          </Typography>
          {selectedUpc ? (
            <Typography variant="body2" color="text.secondary">
              {`UPC ${selectedUpc}`}
            </Typography>
          ) : null}
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RestartAlt />}
              onClick={handleResetSelection}
            >
              {t('correction.replaceDrawer.reset', 'Reset')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddCircleOutline />}
              onClick={() => setProductDialogMode('create')}
              disabled={submitting}
            >
              {t('correction.actions.createProduct', 'Create product')}
            </Button>
            <Button
              variant="outlined"
              startIcon={<EditOutlined />}
              onClick={() => setProductDialogMode('edit')}
              disabled={submitting || !editableProduct}
            >
              {t('correction.actions.editProduct', 'Edit product')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr',
            lg: visibleCandidates.length
              ? 'minmax(0, 1fr) minmax(0, 1fr)'
              : '1fr',
          },
          alignItems: 'start',
        }}
      >
        {visibleCandidates.length ? (
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack spacing={1.25}>
              <Stack spacing={0.25}>
                <Typography variant="subtitle2">
                  {t(
                    'correction.replaceDrawer.quickPicks',
                    'Suggested matches',
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t(
                    'correction.replaceDrawer.quickPicksDescription',
                    'Review possible products before searching from scratch.',
                  )}
                </Typography>
              </Stack>
              <Stack
                spacing={1.25}
                sx={{
                  maxHeight: { lg: 460 },
                  overflowY: { lg: 'auto' },
                  pr: { lg: 0.5 },
                }}
              >
                {visibleCandidates.map(candidate => {
                  const selected = candidate.upcA === selectedUpc
                  const candidateLabel =
                    getCorrectionCandidateDisplayName(candidate) ??
                    candidate.upcA

                  return (
                    <Paper
                      key={candidate.id}
                      variant="outlined"
                      sx={theme => ({
                        p: 1.25,
                        borderColor: selected
                          ? theme.palette.primary.main
                          : theme.palette.divider,
                        backgroundColor: selected
                          ? theme.palette.action.selected
                          : theme.palette.background.paper,
                      })}
                    >
                      <Button
                        fullWidth
                        color="inherit"
                        onClick={() => {
                          setSelectedUpc(candidate.upcA)
                          setSelectedReplacementProduct(null)
                          setProductSearchInput('')
                        }}
                        sx={{
                          p: 0,
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                          sx={{ width: '100%' }}
                        >
                          <ProductImageBox
                            imageUrl={candidate.marketingImageUrl}
                            size={64}
                            alt={candidateLabel}
                            noImageLabel={t(
                              'correction.anchorImage.unavailable',
                              'No image',
                            )}
                            padding={0.5}
                            previewImageSize={320}
                          />
                          <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {candidateLabel}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {`UPC ${candidate.upcA}`}
                            </Typography>
                          </Stack>
                          {selected ? (
                            <Chip
                              label={t('common.selected', 'Selected')}
                              color="primary"
                              size="small"
                            />
                          ) : null}
                        </Stack>
                      </Button>
                    </Paper>
                  )
                })}
              </Stack>
            </Stack>
          </Paper>
        ) : null}

        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Stack spacing={1.25}>
            <Stack spacing={0.25}>
              <Typography variant="subtitle2">
                {t(
                  'correction.replaceDrawer.searchTitle',
                  'Search all products',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.replaceDrawer.searchDescription',
                  'Use search when the suggested products do not show the correct product.',
                )}
              </Typography>
            </Stack>
            <Autocomplete<SearchProduct, false, false, false>
              value={selectedReplacementProduct}
              options={productSearchOptions}
              loading={searchingProducts}
              fullWidth
              getOptionLabel={option => option.upcA}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={options => options}
              inputValue={productSearchInput}
              onInputChange={(_event, nextValue, reason) => {
                if (reason === 'reset' && selectedReplacementProduct) {
                  setProductSearchInput(
                    getSearchProductDisplayName(selectedReplacementProduct) ??
                      selectedReplacementProduct.upcA,
                  )
                  return
                }

                setProductSearchInput(nextValue)
              }}
              onChange={(_event, nextValue) => {
                setSelectedUpc(nextValue?.upcA ?? selectedUpc)
                setSelectedReplacementProduct(nextValue)
              }}
              noOptionsText={
                productSearchInput.trim()
                  ? t('correction.replaceDrawer.noResults', 'No products found')
                  : t(
                      'correction.replaceDrawer.startTyping',
                      'Start typing a product name or UPC',
                    )
              }
              renderInput={params => (
                <TextField
                  {...params}
                  fullWidth
                  label={t(
                    'correction.replaceDrawer.searchLabel',
                    'Search products',
                  )}
                  placeholder={t(
                    'correction.replaceDrawer.searchPlaceholder',
                    'Try a UPC, preferred name, or brand',
                  )}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Search fontSize="small" sx={{ mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => {
                const optionLabel =
                  getSearchProductDisplayName(option) ?? option.upcA
                return (
                  <Box component="li" {...props} key={option.id}>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ py: 0.5 }}
                    >
                      <ProductImageBox
                        imageUrl={option.marketingImageUrl}
                        size={52}
                        alt={optionLabel}
                        noImageLabel={t(
                          'correction.anchorImage.unavailable',
                          'No image',
                        )}
                        padding={0.5}
                        previewImageSize={320}
                      />
                      <ListItemText
                        primary={optionLabel}
                        secondary={`UPC ${option.upcA}`}
                      />
                    </Stack>
                  </Box>
                )
              }}
            />
          </Stack>
        </Paper>
      </Box>
    </Stack>
  )

  const handleProductSaved = React.useCallback(
    async (savedUpc: string) => {
      setSelectedUpc(savedUpc)
      setSelectedReplacementProduct(null)
      setProductSearchInput(savedUpc)
      latestProductSearchRequestRef.current += 1
      const requestId = latestProductSearchRequestRef.current

      await Promise.all([
        refetchProductByUpc({ upc: savedUpc }),
        loadProductSearchOptions(savedUpc, requestId),
      ])
    },
    [loadProductSearchOptions, refetchProductByUpc],
  )

  const pageHeaderActions = React.useMemo(
    () => (
      <Tooltip title={t('correction.help.tooltip', 'How correction works')}>
        <IconButton
          size="small"
          onClick={() => setHelpOpen(true)}
          aria-label={t('correction.help.ariaLabel', 'Open correction help')}
        >
          <HelpOutline fontSize="small" />
        </IconButton>
      </Tooltip>
    ),
    [t],
  )

  usePageHeaderActions(pageHeaderActions)

  return (
    <>
      <Helmet title={t('correction.helmet', 'Correction')} />
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{ md: 'stretch' }}
        >
          <Box
            sx={theme => ({
              flex: '1 1 0%',
              border: '1px solid',
              borderColor: theme.palette.divider,
              borderRadius: 2,
              p: 2,
              backgroundColor: theme.palette.background.paper,
            })}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
            >
              <Box
                sx={{
                  width: 92,
                  flexShrink: 0,
                }}
              >
                {productLoading ? (
                  <Skeleton
                    variant="rounded"
                    width={92}
                    height={92}
                    sx={{ borderRadius: 2 }}
                  />
                ) : null}
                {!productLoading ? (
                  <ProductImageBox
                    imageUrl={anchorImageUrl}
                    size={92}
                    alt={t('correction.anchorImage.alt', {
                      upc: selectedUpc ?? '',
                      defaultValue: `Corrected product for ${selectedUpc ?? ''}`,
                    })}
                    noImageLabel={t(
                      'correction.anchorImage.unavailable',
                      'No image',
                    )}
                    padding={1}
                    previewImageSize={420}
                  />
                ) : null}
              </Box>
              <Stack spacing={0.75} sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedUpc ? (
                    <Chip
                      label={t('correction.summary.upc', {
                        upc: selectedUpc,
                        defaultValue: `UPC ${selectedUpc}`,
                      })}
                      color="warning"
                    />
                  ) : null}
                  {crop?.id ? (
                    <Box
                      sx={theme => ({
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        maxWidth: 176,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        borderRadius: 1.5,
                        px: 1.25,
                        py: 0.875,
                        backgroundColor: theme.palette.background.default,
                        flexShrink: 0,
                      })}
                    >
                      <CopyableIdField
                        label={t('correction.summary.boxId', 'Box ID')}
                        value={crop.id}
                        inline
                        maxWidth={160}
                      />
                    </Box>
                  ) : null}
                  {reviewMetadata?.objectId ? (
                    <Chip
                      label={t('correction.summary.reviewBoxId', {
                        boxId: reviewMetadata.objectId,
                        defaultValue: `Review box ID ${reviewMetadata.objectId}`,
                      })}
                      variant="outlined"
                    />
                  ) : null}
                  {readOnly && reviewMetadata ? (
                    <ReviewVerificationChips
                      isVerified={reviewMetadata.isVerified}
                      verifiedByEmail={reviewMetadata.verifiedByEmail}
                      verifiedAt={reviewMetadata.verifiedAt}
                    />
                  ) : null}
                </Stack>
                {displayedProductLabel ? (
                  <Typography variant="h5" sx={{ maxWidth: 900 }}>
                    {displayedProductLabel}
                  </Typography>
                ) : correction ? (
                  <Typography variant="h5">
                    {t(
                      'correction.summary.noProductSelected',
                      'No product selected yet',
                    )}
                  </Typography>
                ) : null}
                {crop?.verifiedUpc ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('correction.summary.originalVerifiedUpc', {
                      upc: crop.verifiedUpc,
                      defaultValue: `Originally verified as ${crop.verifiedUpc}.`,
                    })}
                  </Typography>
                ) : null}
              </Stack>
            </Stack>
          </Box>
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            flexWrap="wrap"
            useFlexGap
            sx={{ alignItems: 'center' }}
          >
            <Button
              variant="outlined"
              startIcon={<FlagOutlined />}
              onClick={() => setFlagCrop(crop?.id ?? null)}
              disabled={submitting || readOnly}
              sx={{ height: 40 }}
            >
              {t('correction.actions.flagCrop', 'Flag crop')}
            </Button>
            {!readOnly ? (
              <Button
                variant="contained"
                startIcon={<CheckCircleOutline />}
                sx={{ height: 40 }}
                onClick={() => {
                  if (!selectedUpc) return
                  setSubmitError(null)
                  void handleSubmitCorrection(selectedUpc).catch(
                    submitCorrectionError => {
                      console.error(
                        'Failed to submit classification correction',
                        submitCorrectionError,
                      )
                      setSubmitError(
                        submitCorrectionError instanceof Error
                          ? submitCorrectionError.message
                          : t(
                              'correction.submit.failure',
                              'Failed to submit correction',
                            ),
                      )
                    },
                  )
                }}
                disabled={!selectedUpc || submitting || !correction}
              >
                {t('correction.actions.submit', 'Submit correction')}
              </Button>
            ) : null}
          </Stack>
        </Stack>

        {loading && !correction && (
          <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
            <CircularProgress />
          </Box>
        )}

        {error && <Alert severity="error">{error.message}</Alert>}
        {submitError && <Alert severity="error">{submitError}</Alert>}

        {leaseLock && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void handleRetry()}
              >
                {t('common.retry', 'Retry')}
              </Button>
            }
          >
            {leaseLock.message}
          </Alert>
        )}

        {noItems && <Alert severity="info">{noItems.message}</Alert>}

        <ReviewFeedbackNotice
          feedback={correction?.reviewFeedback}
          returnedCount={correction?.returnedFeedbackCount}
        />

        {correction && crop ? (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                lg:
                  isInlineReplacePanel && replaceDrawerOpen && !readOnly
                    ? 'minmax(0, 1fr) minmax(0, 1fr)'
                    : '1fr',
              },
              alignItems: 'start',
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Stack spacing={2}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    width: '100%',
                  }}
                >
                  <FullImageDialog
                    box={crop}
                    open={!!crop}
                    onClose={() => undefined}
                    selectedUpc={selectedUpc}
                    embedded
                    showSelectedProduct={false}
                  />
                </Paper>
              </Stack>
            </Box>
            {isInlineReplacePanel && replaceDrawerOpen && !readOnly ? (
              <Paper
                variant="outlined"
                sx={{
                  position: 'sticky',
                  top: 88,
                  minWidth: 0,
                }}
              >
                {replaceDrawerContent}
              </Paper>
            ) : null}
          </Box>
        ) : null}
      </Stack>
      <FullImageDialog
        box={selectedPreviewBox}
        open={!!selectedPreviewBox}
        onClose={() => setSelectedPreviewBoxId(null)}
        selectedUpc={selectedUpc}
      />
      <Dialog
        open={!isInlineReplacePanel && replaceDrawerOpen && !readOnly}
        onClose={() => setReplaceDrawerOpen(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            width: 'min(92vw, 1100px)',
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>{replaceDrawerContent}</DialogContent>
      </Dialog>
      <FlagItemModal
        open={Boolean(flagCrop)}
        onClose={() => setFlagCrop(null)}
        flagType={UltraFlaggedTypeChoices.CLASSIFICATION_CROP}
        title={t('correction.flagCrop.title', 'Flag Correction Crop')}
        subjectLabel={t('correction.flagCrop.subjectLabel', 'correction crop')}
        onSubmit={async ({ reasonOptionId, moreInfo }) => {
          if (!flagCrop) return

          try {
            const response = await flagClassificationCrop({
              variables: {
                input: {
                  boxId: flagCrop,
                  reasonOptionId,
                  moreInfo: moreInfo ?? null,
                },
              },
            })
            const payload = response.data?.flagClassificationCrop

            if (payload?.__typename !== 'ClassificationFlagCropResult') {
              throw new Error(
                t(
                  'correction.flagCrop.failure',
                  'Flag correction crop did not succeed',
                ),
              )
            }

            await handleCropFlagged(payload.boundingBox.id)
            setFlagCrop(null)
          } catch (flagError) {
            console.error(
              'Failed to finish flagging correction crop',
              flagError,
            )
            throw flagError
          }
        }}
      />
      <ProductDialog
        mode={productDialogMode ?? 'create'}
        open={Boolean(productDialogMode)}
        initialProduct={productDialogMode === 'edit' ? editableProduct : null}
        onClose={() => setProductDialogMode(null)}
        onSaved={handleProductSaved}
      />
      <Dialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          {t('correction.helpDialog.title', 'How Correction Works')}
          <IconButton
            aria-label={t(
              'correction.helpDialog.close.ariaLabel',
              'Close correction help',
            )}
            onClick={() => setHelpOpen(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t(
                  'correction.helpDialog.overview.title',
                  'What This Screen Is For',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.helpDialog.overview.body',
                  'The correction screen is a way to verify products that have been flagged by AI as likely being misclassified. It works similar to the classification screen, but it goes through one bounding box at a time, instead of batches. Your goal is to replace the incorrect product with the correct one, or submit without a replacement if it is already correct.',
                )}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t(
                  'correction.helpDialog.selection.title',
                  'Choosing The Correct Product',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.helpDialog.selection.suggested',
                  'Start with Suggested matches when they look close. If the right product is not there, use Search all products.',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.helpDialog.selection.createEdit',
                  'You can Create product when the product does not exist yet, or Edit product to fix the currently selected product before submitting.',
                )}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t(
                  'correction.helpDialog.submit.title',
                  'Submitting Or Flagging',
                )}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.helpDialog.submit.body',
                  'Submit correction after the correct product is selected. Use Flag crop instead when the crop itself is bad, unclear, or needs manual follow-up rather than a product change.',
                )}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {t('correction.helpDialog.image.title', 'Image Viewer Tips')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t(
                  'correction.helpDialog.image.body',
                  'Use Focus product to recenter the crop and Show full image when you need more context around the shelf or neighboring products.',
                )}
              </Typography>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default Correction
